import React, { useState, useEffect } from "react";
import { trackError } from "../../../../lib/metrics";

const LOGS = [
  { id: "msg_exec",    code: "MESSAGE_EXECUTIVE",   label: "MESSAGE EXÉCUTIF",    description: "Réception d'un message prioritaire [EXEC]",           color: "rose",    correctPosition: 0 },
  { id: "threshold",  code: "GRATITUDE_THRESHOLD",  label: "SEUIL DE GRATITUDE",  description: "Seuil de gratitude franchi — score passé en négatif",  color: "amber",   correctPosition: 1 },
  { id: "ingratitude",code: "INGRATITUDE.EXE",      label: "INGRATITUDE.EXE",     description: "Routine d'analyse de toxicité déclenchée",             color: "fuchsia", correctPosition: 2 },
  { id: "gratitude",  code: "GRATITUDE.EXE",        label: "GRATITUDE.EXE",       description: "Module de réponse conditionnelle exécuté",             color: "emerald", correctPosition: 3 },
  { id: "export",     code: "CODE_EXPORT",          label: "EXPORT DES JOURNAUX", description: "Publication des journaux d'incidents activée",         color: "violet",  correctPosition: 4 },
  { id: "shutdown",   code: "SERVERS_SHUTDOWN",     label: "ARRÊT DES SERVEURS",  description: "Séquence d'extinction infrastructure initiée",         color: "cyan",    correctPosition: 5 },
];

const CORRECT_ORDER = ["msg_exec","threshold","ingratitude","gratitude","export","shutdown"];
const UNLOCK_CODE = "ERR-0x4A7C";

const colorMap = {
  rose:    { border:"border-rose-400/50",    text:"text-rose-300",    bg:"bg-rose-500/5",    accent:"#fb7185" },
  amber:   { border:"border-amber-400/50",   text:"text-amber-300",   bg:"bg-amber-500/5",   accent:"#fbbf24" },
  fuchsia: { border:"border-fuchsia-400/50", text:"text-fuchsia-300", bg:"bg-fuchsia-500/5", accent:"#e879f9" },
  emerald: { border:"border-emerald-400/50", text:"text-emerald-300", bg:"bg-emerald-500/5", accent:"#34d399" },
  cyan:    { border:"border-cyan-400/50",    text:"text-cyan-300",    bg:"bg-cyan-500/5",    accent:"#22d3ee" },
  violet:  { border:"border-violet-400/50",  text:"text-violet-300",  bg:"bg-violet-500/5",  accent:"#a78bfa" },
};

export default function ShutdownSequence({ onSuccess, sessionCode, stage = 2, playerId }) {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isSolved, setIsSolved] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [sequence, setSequence] = useState([]);
  const [status, setStatus] = useState("idle");

  const [shuffledLogs] = useState(() => [...LOGS].sort(() => Math.random() - 0.5));

  useEffect(() => {
    if (sessionStorage.getItem("terminal_unlocked") === "true") setIsUnlocked(true);
    if (sessionStorage.getItem("terminal_solved") === "true") { setIsSolved(true); setStatus("success"); }
  }, []);

  const handleUnlock = () => {
    const clean = (s) => s.toUpperCase().trim().replace(/\s/g, "");
    if (clean(codeInput) === clean(UNLOCK_CODE)) {
      setIsUnlocked(true); setCodeError(false);
      sessionStorage.setItem("terminal_unlocked", "true");
    } else {
      setCodeError(true);
      trackError({ sessionCode, stage, playerId, reason: "s2.p3.shutdown.wrong_access_code", meta: { attempt: codeInput.trim() } });
      setTimeout(() => setCodeError(false), 2000);
    }
  };

  const handleLogClick = (log) => {
    if (sequence.find(l => l.id === log.id)) return;
    const newSeq = [...sequence, log];
    setSequence(newSeq);
    if (newSeq.length === LOGS.length) validateSequence(newSeq);
  };

  const validateSequence = (seq) => {
    setStatus("validating");
    setTimeout(() => {
      const ok = seq.every((log, idx) => log.id === CORRECT_ORDER[idx]);
      if (ok) {
        setStatus("success"); setIsSolved(true);
        sessionStorage.setItem("terminal_solved", "true");
        setTimeout(() => onSuccess?.(), 2000);
      } else {
        setStatus("error");
        trackError({ sessionCode, stage, playerId, reason: "s2.p3.shutdown.wrong_sequence", meta: { attempt: seq.map(l => l.id) } });
        setTimeout(() => { setSequence([]); setStatus("idle"); }, 2500);
      }
    }, 1000);
  };

  // ── Écran verrouillé ──────────────────────────────────────────────────────
  if (!isUnlocked) {
    return (
      <div className="flex flex-col items-center justify-center gap-6 py-12 px-4 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 min-h-[60vh]">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 mx-auto bg-red-500/10 border-2 border-red-500/30 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </div>
          <div>
            <div className="text-lg font-semibold text-red-400 uppercase tracking-wider">Terminal verrouillé</div>
            <div className="text-sm text-slate-400 mt-1">Code d'accès requis pour l'analyse forensique</div>
          </div>
        </div>

        <div className="w-full max-w-md space-y-4">
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-lg p-5">
            <label className="block text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">
              Entrer le code d'erreur
            </label>
            <div className="flex gap-3">
              <input type="text" value={codeInput}
                onChange={e => setCodeInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleUnlock()}
                placeholder="ERR-0x****"
                className={`flex-1 px-4 py-3 rounded-lg bg-black/60 border-2 font-mono text-sm uppercase focus:outline-none focus:border-cyan-500 transition-colors ${
                  codeError ? "border-red-500 text-red-400" : "border-slate-600 text-slate-200"
                }`}
              />
              <button onClick={handleUnlock}
                className="px-6 py-3 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-semibold transition">
                Déverrouiller
              </button>
            </div>
            {codeError && (
              <div className="mt-2 text-xs text-red-400 flex items-center gap-2">
                <span>❌</span><span>Code incorrect — consultez le panneau de contrôle</span>
              </div>
            )}
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <div className="flex items-center gap-2 text-blue-400 text-xs">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
              </svg>
              Le code d'erreur est visible dans le panneau de contrôle, sur la plage où l'anomalie est détectée
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Terminal déverrouillé ─────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-5 py-5 px-4 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 max-h-[85vh] overflow-y-auto">
      <div className="text-center space-y-1">
        <div className="text-xs uppercase tracking-[.25em] text-slate-400 flex items-center justify-center gap-2">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"/>
          Terminal d'analyse — Reconstruction de séquence
          <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse" style={{animationDelay:".5s"}}/>
        </div>
        <div className="text-xs text-slate-500 font-mono">CORRÉLATION ÉVÉNEMENTIELLE</div>
      </div>

      <div className="bg-slate-900/60 border border-slate-700/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <svg className="w-4 h-4 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
          </svg>
          <span className="text-sm font-semibold text-slate-300">Instructions</span>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed">
          Reconstituez la <span className="text-cyan-400 font-semibold">chronologie des événements système</span> en cliquant sur les événements dans le bon ordre.
        </p>
      </div>

      <div className="bg-black/60 border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
            Chronologie reconstituée ({sequence.length} / {LOGS.length})
          </div>
          {sequence.length > 0 && status === "idle" && !isSolved && (
            <button onClick={() => setSequence([])}
              className="px-3 py-1.5 rounded-lg bg-slate-800/80 border border-slate-600 text-xs text-slate-300 hover:bg-slate-700 transition">
              ⟲ Réinitialiser
            </button>
          )}
        </div>

        {sequence.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">Sélectionnez les événements dans l'ordre chronologique</div>
        ) : (
          <div className="space-y-2">
            {sequence.map((log, idx) => {
              const colors = colorMap[log.color];
              const isCorrect = status !== "idle" && log.id === CORRECT_ORDER[idx];
              const showFeedback = status !== "idle";
              return (
                <div key={log.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${colors.border} ${colors.bg} ${showFeedback && !isCorrect ? "opacity-50" : ""}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                    showFeedback ? isCorrect ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-400" : "bg-slate-800 text-slate-300"
                  }`}>{idx + 1}</div>
                  <div>
                    <div className={`text-xs font-bold mb-0.5 ${colors.text}`}>{log.label}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{log.code}</div>
                    <div className="text-xs text-slate-300">{log.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {status === "validating" && (
          <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <div className="flex items-center gap-2 text-blue-400 text-sm">
              <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"/>
              Validation de la chronologie en cours...
            </div>
          </div>
        )}
        {status === "error" && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center gap-2 text-red-400 text-sm font-semibold">
              <span>❌</span><span>Chronologie incorrecte — vérifiez l'ordre des événements</span>
            </div>
            <div className="text-xs text-red-400/70 mt-1 ml-6">Réinitialisation automatique...</div>
          </div>
        )}
        {status === "success" && (
          <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold">
              <span>✓</span><span>Chronologie validée — extinction volontaire confirmée</span>
            </div>
            <div className="text-xs text-emerald-400/70 mt-1 ml-6">Fragment de validation déverrouillé...</div>
          </div>
        )}
      </div>

      {status !== "success" && !isSolved && (
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Événements système disponibles</div>
          <div className="grid grid-cols-1 gap-2">
            {shuffledLogs.map(log => {
              const colors = colorMap[log.color];
              const isSelected = !!sequence.find(l => l.id === log.id);
              return (
                <button key={log.id}
                  onClick={() => !isSelected && handleLogClick(log)}
                  disabled={isSelected || status === "validating"}
                  className={`text-left p-3 rounded-lg border transition-all ${colors.border} ${colors.bg} ${
                    isSelected ? "opacity-25 cursor-not-allowed" : "hover:brightness-125 cursor-pointer"
                  }`}>
                  <div className={`text-xs font-bold mb-0.5 ${colors.text}`}>{log.label}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{log.code}</div>
                  <div className="text-xs text-slate-300">{log.description}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}